import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import { PayoutsPageHeader } from "./payouts-page-header";
import { PayoutSummaryCards } from "./payout-summary-cards";
import { PayoutSettlementBanner } from "./payout-settlement-banner";
import { PayoutHistoryToolbar } from "./payout-history-toolbar";
import { PayoutHistoryTable } from "./payout-history-table";
import { PayoutsPagination } from "./payouts-pagination";
import { AddAccountModal } from "./add-account-modal";
import { WithdrawModal } from "./withdraw-modal";
import { WALLET_SUMMARY } from "./payouts-mock-data";
import {
  filterPayoutRows,
  mapWalletHistoryItemToRow,
  paginateSlice,
  totalPagesFor,
} from "./payouts-utils";
import {
  FETCH_DEFAULT_WALLET_HISTORIES,
  GET_ALL_PUSRSES,
} from "../../../../Utils/Queries";

function toCents(dollars) {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

const PAGE_SIZE = 10;

export default function SellerPayoutsPage() {
  const [hasSettlementAccount, setHasSettlementAccount] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [accountToast, setAccountToast] = useState(false);
  const [withdrawToast, setWithdrawToast] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, loading, error } = useQuery(FETCH_DEFAULT_WALLET_HISTORIES, {
    variables: { page: null, limit: null },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const { data: walletData, loading: walletLoading } = useQuery(
    GET_ALL_PUSRSES,
    { fetchPolicy: "cache-and-network" },
  );

  const walletSummary = useMemo(() => {
    const wallets = walletData?.getWallets ?? [];
    const wallet =
      wallets.find((w) => w?.isDefault) ?? wallets[0] ?? null;
    return {
      spendingCents: toCents(wallet?.spendingBalance),
      earningCents: toCents(wallet?.earningBalance),
      pendingCents: WALLET_SUMMARY.pendingCents,
      settledCents: WALLET_SUMMARY.settledCents,
      currencySymbol: wallet?.currency?.symbol ?? "$",
      raw: wallet,
    };
  }, [walletData?.getWallets]);

  const apiRows = useMemo(() => {
    const items = data?.fetchDefaultWalletHistories?.items ?? [];
    return items.map(mapWalletHistoryItemToRow).filter(Boolean);
  }, [data?.fetchDefaultWalletHistories?.items]);

  const filtered = useMemo(
    () => filterPayoutRows(apiRows, search),
    [apiRows, search]
  );

  const totalPages = totalPagesFor(filtered.length, PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => paginateSlice(filtered, safePage, PAGE_SIZE),
    [filtered, safePage]
  );

  const isInitialLoading = loading && apiRows.length === 0;

  const handleAccountAdded = () => {
    setHasSettlementAccount(true);
    setAccountToast(true);
  };

  return (
    <div className="pb-10 pt-10 sm:pb-12 sm:pt-10 md:pt-6">
      <PayoutsPageHeader
        onExport={() => {}}
        onWithdraw={() => setWithdrawOpen(true)}
      />

      <PayoutSummaryCards
        summary={walletSummary}
        walletLoading={walletLoading && !walletData}
      />

      <PayoutSettlementBanner
        hasAccount={hasSettlementAccount}
        onAddAccount={() => setAddAccountOpen(true)}
      />

      <div className="rounded-[14px] border border-[#EFEFEF] bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)] sm:p-5 md:p-6 lg:p-8">
        <PayoutHistoryToolbar
          search={search}
          onSearchChange={setSearch}
          onFilterClick={() => {}}
        />
        <div>
          {isInitialLoading ? (
            <div className="flex min-h-[240px] items-center justify-center">
              <CircularProgress size={28} sx={{ color: "#2E1834" }} />
            </div>
          ) : error ? (
            <div className="flex min-h-[240px] items-center justify-center px-4">
              <p className="my-0 text-center text-[13px] font-medium text-[#9CA3AF]">
                We couldn’t load payout history right now. Please try again later.
              </p>
            </div>
          ) : (
            <PayoutHistoryTable rows={pageRows} />
          )}
        </div>
        <PayoutsPagination
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>

      <AddAccountModal
        open={addAccountOpen}
        onClose={() => setAddAccountOpen(false)}
        onSuccess={handleAccountAdded}
      />

      <WithdrawModal
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        availableCents={walletSummary.earningCents}
        onRequestAddAccount={() => setAddAccountOpen(true)}
        onWithdraw={() => setWithdrawToast(true)}
      />

      <Snackbar
        open={accountToast}
        autoHideDuration={4000}
        onClose={() => setAccountToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAccountToast(false)}
          severity="success"
          variant="filled"
          sx={{
            bgcolor: "#1F2937",
            color: "#fff",
            borderRadius: "12px",
            fontWeight: 600,
          }}
        >
          Account added successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={withdrawToast}
        autoHideDuration={4000}
        onClose={() => setWithdrawToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setWithdrawToast(false)}
          severity="success"
          variant="filled"
          sx={{
            bgcolor: "#1F2937",
            color: "#fff",
            borderRadius: "12px",
            fontWeight: 600,
          }}
        >
          Withdrawal request submitted.
        </Alert>
      </Snackbar>
    </div>
  );
}
